import { createContext, ReactNode, useContext, useEffect, useReducer, useState } from 'react'
import Cookies from 'js-cookie'
// routes
import { useSearchParams } from 'react-router-dom'

// @types
import { ActionMap, AuthState, AWSCognitoContextType } from '../contexts/infer-types/authentication'
//
import customAxios from 'utils/axiosCustom'
import { useSocketConnection } from 'hooks/useSocketConnection'
import { ConfigContext } from './ConfigurationContext'
// ----------------------------------------------------------------------

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false
}

export enum Types {
  auth = 'AUTHENTICATE',
  logout = 'LOGOUT'
}

type AwsAuthPayload = {
  [Types.auth]: {
    isAuthenticated: boolean
  }
  [Types.logout]: undefined
}

type AwsActions = ActionMap<AwsAuthPayload>[keyof ActionMap<AwsAuthPayload>]

const reducer = (state: AuthState, action: AwsActions) => {
  if (action.type === 'AUTHENTICATE') {
    const { isAuthenticated } = action.payload
    return {
      ...state,
      isAuthenticated,
      isInitialized: true
    }
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false
    }
  }
  return state
}

const AuthContext = createContext<AWSCognitoContextType | null>(null)
AuthContext.displayName = 'AWSCognito context'

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [searchParams] = useSearchParams()
  const { onSocketClosed } = useSocketConnection()

  const cognitoConfig = useContext(ConfigContext)
  const [clientID] = useState(cognitoConfig?.REACT_APP_AWS_COGNITO_CLIENT_ID || '')
  /*@ts-ignore*/
  const redirectUrl =
    /*@ts-ignore*/
    window.brand === 'issuer'
      ? `${cognitoConfig?.REACT_APP_DOMAIN}/nab_customer.html`
      : cognitoConfig.REACT_APP_AWS_COGNITO_REDIRECT_URI
  const [tokenUrl] = useState(cognitoConfig?.REACT_APP_AWS_COGNITO_USER_POOL_AUTH_TOKEN || '')
  useEffect(() => {
    const fetchData = async () => {
      // Check if refresh_token exists in the cookie
      const jwtToken = Cookies.get('token')
      if (jwtToken) {
        const tokenParts = jwtToken.split('.')
        const payloadDecoded = Buffer.from(tokenParts[1], 'base64').toString()

        const payload = JSON.parse(payloadDecoded)

        const expirationTime = new Date(payload.exp * 1000) // Convert to milliseconds
        const currentTime = new Date()

        if (currentTime >= expirationTime) {
          logout()
        } else {
          dispatch({
            type: Types.auth,
            payload: { isAuthenticated: true }
          })
        }
      } else {
        // Fetch the code from the URL
        const urlParams = searchParams
        const code = urlParams.get('code')

        if (code) {
          try {
            const data = {
              grant_type: 'authorization_code',
              client_id: clientID, // your app client id
              code: code, // the authorization code
              redirect_uri: redirectUrl
            }

            // Make the POST call to the backend
            const response = await customAxios.post(
              `${tokenUrl}/oauth2/token`,
              new URLSearchParams(data),
              {
                headers: {
                  'Content-Type': 'application/x-www-form-urlencoded'
                }
              }
            )
            // Get the JWT and refresh_token from the response
            const { id_token } = response.data

            // Save the JWT and refresh_token in the cookies
            Cookies.set('token', id_token)
          } catch (error) {
            // Handle error or redirect to login page
            logout()
          }
        } else {
          // Code not present in the URL, handle accordingly
          // Handle error or redirect to login page
          logout()
        }
      }
    }
    const timer = setInterval(() => {
      fetchData()
    }, 2000)
    return () => clearInterval(timer)
  }, [])

  // same thing here
  const logout = async () => {
    // Do some logout stuff

    dispatch({
      type: Types.auth,
      payload: { isAuthenticated: false }
    })
    onSocketClosed()
    Cookies.remove('token')
    localStorage.getItem('redux-root') && localStorage.removeItem('redux-root')
    localStorage.getItem('redux-user') && localStorage.removeItem('redux-user')
    localStorage.getItem('redux-guarantee') && localStorage.removeItem('redux-guarantee')
    localStorage.getItem('redux-contractHistory') &&
    localStorage.removeItem('redux-contractHistory')
    localStorage.getItem('redux-socket') && localStorage.removeItem('redux-socket')
    localStorage.getItem('redux-admin') && localStorage.removeItem('redux-admin')
    localStorage.getItem('redux-actions') && localStorage.removeItem('redux-actions')
    localStorage.getItem('redux-createGx') && localStorage.removeItem('redux-createGx')
    

    window.location.href = `${cognitoConfig.REACT_APP_AWS_COGNITO_USER_POOL_AUTH_TOKEN}/logout?client_id=${cognitoConfig.REACT_APP_AWS_COGNITO_CLIENT_ID}&logout_uri=${cognitoConfig.REACT_APP_AWS_COGNITO_LOGOUT_URI}&redirect_uri=${cognitoConfig.REACT_APP_AWS_COGNITO_REDIRECT_URI}&response_type=${cognitoConfig.REACT_APP_AWS_COGNITO_OAUTH_RESPONSE_TYPE}`
    window.history.replaceState(null, '', '/')
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export { AuthContext, AuthProvider }
